import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import SafetyRolesExplainer from 'features/compliance/SafetyRolesExplainer'
import SafetyRoleEditor from 'features/accessControl/safetyRoles/SafetyRoleEditor'
import useSafetyRoleCandidates from 'features/accessControl/safetyRoles/useSafetyRoleCandidates'
import useDutyholderOrganisations from 'features/accessControl/safetyRoles/useDutyholderOrganisations'
import { fetchBpRoutine } from 'features/bpDetails/ducks/actions'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { ink, line, radius, surface, font } from 'features/dashboard/tokens'

const ACTION_LABEL = { missing: 'Set this role', na: 'Change', assigned: 'Change' }

const SafetyRolesSetup = ({ bpId, roles = [], onChanged }) => {
  const dispatch = useDispatch()
  const selectedBp = useSelector(getSelectedBp)
  const [editing, setEditing] = useState(null)
  const scope = useMemo(() => ({ type: 'bp', id: bpId }), [bpId])
  const candidates = useSafetyRoleCandidates(scope)
  const organisations = useDutyholderOrganisations()

  useEffect(() => {
    if (bpId && selectedBp?.id !== bpId) {
      dispatch(fetchBpRoutine({ id: bpId }))
    }
  }, [bpId, selectedBp, dispatch])

  const refresh = () => {
    dispatch(fetchBpRoutine({ id: bpId }))
    onChanged?.()
  }

  const bpName = selectedBp?.id === bpId ? selectedBp?.name : null

  return (
    <Card>
      <Head>
        <HeadText>
          <Label>Key safety roles</Label>
          <Title>Who is responsible for this building's safety?</Title>
          <Sub>
            Name the person or organisation that holds each role, or mark it not applicable. This won't block
            your setup. It's a prompt to keep the record complete.
          </Sub>
        </HeadText>
        <SafetyRolesExplainer align='left' />
      </Head>

      <RoleList>
        {roles.map(role => {
          const state = role.state
          const assignees = (role.assignees || []).filter(a => a && a.name)
          const open = editing === role.key
          return (
            <React.Fragment key={role.key}>
              <RoleRow $state={state}>
                <RoleMain>
                  <RoleName>{role.label}</RoleName>
                  {state === 'assigned' && (
                    <PersonChips>
                      {assignees.map(a => (
                        <PersonChip key={a.organisationLinkId || a.relationId || a.name}>
                          {a.type === 'organisation'
                            ? <ApartmentRoundedIcon style={{ fontSize: 14, color: RAG.low.fg }} />
                            : <CheckCircleRoundedIcon style={{ fontSize: 14, color: RAG.low.fg }} />}
                          {a.name}
                        </PersonChip>
                      ))}
                      {assignees.length === 0 && (
                        <StateChip $tint={RAG.low.fg}>
                          <CheckCircleRoundedIcon style={{ fontSize: 14 }} /> Invited
                        </StateChip>
                      )}
                    </PersonChips>
                  )}
                  {state === 'na' && (
                    <StateChip $tint={ink.mute}>
                      <RemoveCircleOutlineRoundedIcon style={{ fontSize: 14 }} />
                      Not applicable
                    </StateChip>
                  )}
                  {state === 'missing' && (
                    <StateChip $tint={RAG.medium.fg}>
                      <ErrorOutlineRoundedIcon style={{ fontSize: 14 }} />
                      Not set
                    </StateChip>
                  )}
                </RoleMain>

                <RoleActions>
                  {state === 'missing' && !open ? (
                    <PrimaryBtn type='button' aria-expanded={open} onClick={() => setEditing(role.key)}>
                      {ACTION_LABEL.missing}
                    </PrimaryBtn>
                  ) : (
                    <GhostBtn type='button' aria-expanded={open} onClick={() => setEditing(open ? null : role.key)}>
                      {open ? 'Close' : ACTION_LABEL[state] || 'Change'}
                    </GhostBtn>
                  )}
                </RoleActions>
              </RoleRow>
              {open && (
                <SafetyRoleEditor
                  key={role.key}
                  scope={scope}
                  roleKey={role.key}
                  roleLabel={role.label}
                  targetIds={[bpId]}
                  targetLabel={bpName || 'this building'}
                  bpId={bpId}
                  state={state}
                  holders={assignees}
                  candidates={candidates}
                  organisations={organisations}
                  onApplied={refresh}
                  onCancel={() => setEditing(null)}
                  idPrefix={`compliance-${role.key}`}
                />
              )}
            </React.Fragment>
          )
        })}
      </RoleList>
    </Card>
  )
}

export default SafetyRolesSetup

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 18px;
  margin-bottom: 18px;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-left: 3px solid ${RAG.medium.fg};
  border-radius: ${radius.md};
`

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const HeadText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const Label = styled.span`
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
  color: ${ink.mute};
`

const Title = styled.span`
  font-size: ${font.emphasis};
  font-weight: 700;
  color: ${ink.base};
`

const Sub = styled.span`
  font-size: ${font.caption};
  line-height: 1.45;
  color: ${ink.mute};
`

const RoleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const RoleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  background: ${({ $state }) => ($state === 'missing' ? `${RAG.medium.fg}0a` : surface.card)};
`

const RoleMain = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
`

const RoleName = styled.span`
  font-size: ${font.small};
  font-weight: 700;
  color: ${ink.base};
`

const StateChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: ${radius.pill};
  background: ${({ $tint }) => `${$tint}14`};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  color: ${({ $tint }) => $tint};
  font-size: ${font.caption};
  font-weight: 700;
`

const PersonChips = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
`

const PersonChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 6px 3px 10px;
  border-radius: ${radius.pill};
  background: ${`${RAG.low.fg}14`};
  border: 1px solid ${`${RAG.low.fg}33`};
  color: ${RAG.low.fg};
  font-size: ${font.caption};
  font-weight: 700;
`

const RoleActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const PrimaryBtn = styled.button`
  min-height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: ${radius.pill};
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 160ms ease;

  &:hover { filter: brightness(1.06); }
`

const GhostBtn = styled.button`
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.pill};
  background: ${surface.card};
  color: ${ink.base};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 160ms ease, color 160ms ease;

  &:disabled { opacity: 0.55; cursor: default; }
  &:hover:not(:disabled) { border-color: ${ink.mute}; }
`
